/**
 * F071 — parse a Marshmallow/flask-smorest 422 body into per-field errors.
 *
 * smorest's webargs handler emits `{ code: 422, status: "...", errors:
 * { json: { field: ["msg"], list: { "1": { sub: ["msg"] } } } } }`. The
 * `errors` payload rides on `ApiError.errors` (lib/api.ts). This module
 * flattens it into `path → detail` (`faq.1.answer`) so editors can point at
 * the offending field instead of dead-ending on `common.error_generic`.
 */
import { ApiError } from '@/lib/api';

/** webargs location keys — a single-key wrapper at the root is unwrapped. */
const KNOWN_LOCATIONS = new Set([
  'json',
  'query',
  'form',
  'files',
  'headers',
  'cookies',
  'path',
  'view_args',
]);

export interface FieldErrorDetail {
  /** Parsed from Marshmallow Length messages; absent for non-length errors. */
  maxLength?: number;
}

export type FieldErrorMap = Record<string, FieldErrorDetail>;

const LENGTH_MESSAGE_RE = /\b(?:maximum length|between \d+ and) (\d+)\b/;

function detailFromMessages(messages: string[]): FieldErrorDetail {
  for (const message of messages) {
    const match = LENGTH_MESSAGE_RE.exec(message);
    if (match) return { maxLength: Number(match[1]) };
  }
  return {};
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function walk(node: Record<string, unknown>, prefix: string, out: FieldErrorMap): void {
  for (const [key, value] of Object.entries(node)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isStringArray(value)) {
      out[path] = detailFromMessages(value);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      walk(value as Record<string, unknown>, path, out);
    }
  }
}

/**
 * Extract per-field errors from a 422 `ApiError`. Returns `null` when the
 * error isn't a 422 or carries no usable field errors — callers fall back to
 * the generic message in that case.
 */
export function extractKbFieldErrors(err: unknown): FieldErrorMap | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  if (!err.errors || typeof err.errors !== 'object' || Array.isArray(err.errors)) return null;

  let root = err.errors as Record<string, unknown>;
  const rootKeys = Object.keys(root);
  if (
    rootKeys.length === 1 &&
    KNOWN_LOCATIONS.has(rootKeys[0]) &&
    root[rootKeys[0]] &&
    typeof root[rootKeys[0]] === 'object'
  ) {
    root = root[rootKeys[0]] as Record<string, unknown>;
  }

  const out: FieldErrorMap = {};
  walk(root, '', out);
  return Object.keys(out).length > 0 ? out : null;
}
